// SnackbarContext.tsx
import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { WSSnackBar } from './WSSnackbar'; // Adjust the import according to your file structure

interface SnackbarContextProps {
  showMessage: (message: unknown, severity?: "error" | "warning" | "info" | "success") => void;
}

const toReadableMessage = (message: unknown): string => {
  if (message == null) return "";
  if (typeof message === "string") return message;
  if (typeof message === "number" || typeof message === "boolean") return String(message);
  if (Array.isArray(message)) {
    return message
      .map((item) => {
        if (item && typeof item === "object") {
          const rec = item as Record<string, unknown>;
          if (typeof rec.msg === "string") {
            const locArr = Array.isArray(rec.loc) ? (rec.loc as unknown[]) : [];
            const locParts = locArr.filter((p) => p !== "body");
            const loc = locParts.length ? ` (${locParts.join(".")})` : "";
            return `${rec.msg}${loc}`;
          }
          return JSON.stringify(item);
        }
        return String(item);
      })
      .join("; ");
  }
  if (typeof message === "object") {
    const rec = message as Record<string, unknown>;
    if (typeof rec.msg === "string") return rec.msg;
    if (typeof rec.message === "string") return rec.message;
    try {
      return JSON.stringify(message);
    } catch {
      return "Operation failed";
    }
  }
  return String(message);
};

const SnackbarContext = createContext<SnackbarContextProps | undefined>(undefined);

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

interface SnackbarProviderProps {
  children: ReactNode;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<"error" | "warning" | "info" | "success">("info");

  const handleClose = () => {
    setSnackbarOpen(false);
  };

  const showMessage = useCallback((message: unknown, severity: "error" | "warning" | "info" | "success" = 'info', autoClose: boolean = true) => {
    setSnackbarMessage(toReadableMessage(message));
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
    if (autoClose === true) {
      setTimeout(() => {
        setSnackbarOpen(false);
      }, 5000);
    }
  }, []);

  return (
    <SnackbarContext.Provider value={{ showMessage }}>
      {children}
      <WSSnackBar
        message={snackbarMessage}
        open={snackbarOpen}
        onClose={handleClose}
        severity={snackbarSeverity}
      />
    </SnackbarContext.Provider>
  );
};
